import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { Avatar, Box, Divider, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CSS } from '@dnd-kit/utilities';
import {
  ResourceField,
  useResourceForm,
} from '../hooks/useResourceForm/useResourceForm';
import { ListingResource, MediaField } from '../../../system/field';
import { useListingStore } from '@brightmls/nextgen.lae.flc';
import CircularProgress from '@mui/material/CircularProgress';
import { generateDataTestId, toHTTPSURL } from '../../../utils';
import { MediaResource as ResoMediaResource } from '../../../reso/resources/MediaResource';
import { useResoResource } from '../../../reso/useResoResource';
import Bookmark from '@mui/icons-material/Bookmark';
import MediaEditDeleteButtons from './MediaEditDeleteButtons';
import { DragPlaceholder } from './DragPlaceholder';

const PhotoFields: ResourceField[] = [
  {
    Key: '$',
    Layout: 'single',
    Control: 'PhotoPreview',
    Metadata: {
      Key: '$',
      Type: 'Edm.String',
    },
  },
  {
    Key: 'MEDIA_SHORT_DESCRIPTION',
    Label: 'Description',
    Layout: 'single',
    Control: 'TextArea',
    ForceNeverDisabled: true,
    Metadata: {
      Key: 'MEDIA_SHORT_DESCRIPTION',
      Type: 'Edm.String',
      Definition: {
        DataType: 'string',
        MaxLength: '50',
      },
    },
  },
  {
    Key: '$',
    Layout: 'single',
    Control: 'PhotoMetadata',
    Metadata: {
      Key: '$',
      Type: 'Edm.String',
    },
  },
];

const PhotoTile: React.FC<any> = (props: any) => {
  const {
    file: mediaData,
    isPrimary,
    onDeletePhoto: deletePhoto,
    selectedFiles,
    isShowDescriptions,
    onAddPhotoToSelected,
    isDraggedWithMultiple,
    isGeneratingDescription,
    isPartOfCurrentBatchGenerating,
    cancelGeneration,
    isSortingDisabled,
    isSmallDisplay,
    isKeepWhenClosed,
    isDragging,
    updateOffMarketKeys,
    dragAttributes,
    dragListeners,
    dragRef,
    dragStyle,
    isOverMainPosition,
    handleOwnDrag = true,
  } = props;

  const isSelected = selectedFiles.includes(mediaData.MED_VW_KEY);

  const isGenerating =
    isGeneratingDescription(mediaData?.MED_VW_KEY) ||
    (!mediaData.MEDIA_SHORT_DESCRIPTION &&
      isPartOfCurrentBatchGenerating(mediaData?.MED_VW_KEY));

  const sortableProps = useSortable({
    id: mediaData.MEDIA_ITEM_NUMBER.toString(),
    animateLayoutChanges: () => false,
    disabled: !handleOwnDrag || isSortingDisabled, // Disable if not handling own drag
  });

  // Use provided props if available, otherwise use sortable props
  const attributes = dragAttributes || sortableProps.attributes;
  const listeners = dragListeners || sortableProps.listeners;
  const setNodeRef = dragRef || sortableProps.setNodeRef;
  const transform = sortableProps.transform;
  const transition = sortableProps.transition;

  const style: CSSProperties = useMemo(
    () => ({
      ...(dragStyle || {
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition,
      }),
      flexGrow: isSmallDisplay ? 1 : 0,
      touchAction: 'none', // REQUIRED for touch devices - prevents scrolling during drag
      cursor: isDragging ? 'grabbing' : 'grab',
    }),
    [dragStyle, transform, transition, isSmallDisplay, isDragging],
  );

  // Get the media resource manager for direct API calls
  const { resourceManager: mediaManager } = useResoResource<ResoMediaResource>(
    ListingResource.Media,
  );
  const [isConfirmCancel, setIsConfirmCancel] = useState(false);
  const { openResourceForm } = useResourceForm();

  const saveHook = useCallback(
    async (updatedFile: any) => {
      // Get CWA manager for API access
      const cwaManager = mediaManager.cwa();

      // Get current state from the store
      const state = useListingStore.getState();
      const currentMediaList: any = state.getResource(ListingResource.Media);

      // Find the media item we're updating
      const itemIndex = currentMediaList.findIndex(
        (item: any) => item.MEDIA_ITEM_NUMBER === mediaData.MEDIA_ITEM_NUMBER,
      );

      if (itemIndex === -1) return updatedFile;

      // For simple updates (like description changes), just update the one resource
      const resourceToUpdate = new ResoMediaResource({
        ...updatedFile,
        [MediaField.MED_VW_KEY]: updatedFile.MED_VW_KEY,
      });

      // Update via API
      await cwaManager.update(resourceToUpdate);

      return updatedFile;
    },
    [mediaManager, mediaData],
  );

  const showEditSidePanel = useCallback(() => {
    const state = useListingStore.getState();
    const currentMediaList: any = state.getResource(ListingResource.Media);

    const itemIndex = currentMediaList.findIndex(
      (item: any) => item.MEDIA_ITEM_NUMBER === mediaData.MEDIA_ITEM_NUMBER,
    );

    openResourceForm(
      {
        fields: PhotoFields,
        resourceType: ListingResource.Media,
        resourceIndex: itemIndex,
        internalResourceManagement: true,
      },
      {
        title: 'Edit Photo',
      },
      saveHook,
      mediaData,
    );
  }, [mediaData, openResourceForm, saveHook]);

  if (!mediaData) return null;

  const onDeletePhoto = () => {
    if (isGenerating) {
      cancelGeneration(mediaData.MED_VW_KEY);
    }
    if (isKeepWhenClosed) {
      updateOffMarketKeys([]);
    }
    setIsConfirmCancel(false);
    deletePhoto(mediaData.MED_VW_KEY);
  };

  let dnd = !isSortingDisabled ? listeners : {};

  if (isDragging) {
    return (
      <div ref={setNodeRef} {...attributes} {...dnd} style={style}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DragPlaceholder
            isOverMainPosition={isOverMainPosition}
            isShowDescriptions={isShowDescriptions}
            isSmallDisplay={isSmallDisplay}
          />
        </Box>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} {...attributes} {...dnd} style={style}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'row',
          mx: isSmallDisplay ? '0' : '12px',
          my: '12px',
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            boxShadow: isSelected
              ? '0px 0px 1px 4px rgba(128, 148, 214, 1)'
              : 'none',
            border: 1,
            borderColor: 'grey.400',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'white',
            minWidth: { xs: '300px', sm: 'unset' },
            width: {
              xs: '300px',
              sm: '350px',
              md: '380px', // the middle value is arbitrary, feel free to change. Note that width below 329 messes with AI description truncation
            },
            position: 'relative',
          }}
        >
          {isPrimary && (
            <Typography
              sx={{
                zIndex: '2',
                position: 'absolute',
                color: 'white',
                ml: 1,
                mt: 1,
                px: 1,
                backgroundColor: 'red',
                display: isPrimary ? '' : 'none',
              }}
            >
              Main Photo
            </Typography>
          )}
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
            {isKeepWhenClosed && (
              <Bookmark
                sx={{
                  zIndex: '2',
                  position: 'absolute',
                  color: 'primary.main',
                  mr: 1,
                  mt: 1,
                  right: isSelected ? '30px' : 0,
                  stroke: 'white',
                  strokeWidth: 1,
                }}
              />
            )}
            <CheckCircleIcon
              sx={{
                zIndex: '2',
                position: 'absolute',
                color: 'success.main',
                mr: 1,
                mt: 1,
                border: 1,
                borderRadius: 100,
                borderColor: 'white',
                backgroundColor: 'white',
                display: isSelected ? '' : 'none',
              }}
            />
          </Box>
          <Avatar
            sx={{
              width: '100%',
              height: '200px',
            }}
            variant="square"
            photo-key={mediaData.MED_VW_KEY ?? 'a'}
            src={toHTTPSURL(mediaData.MEDIA_URL_MEDIUM)}
            onClick={onAddPhotoToSelected}
          />
          {isShowDescriptions && (
            <Box
              sx={{
                px: 3,
                mt: 2,
                mb: 2,
                height: '30px',
                maxHeight: '30px',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '2px',
                  '&:hover': {
                    background: '#666',
                  },
                },
                // Firefox support
                scrollbarWidth: 'thin',
                scrollbarColor: '#888 #f1f1f1',
              }}
            >
              <Typography
                data-testid={generateDataTestId(
                  `photo-${mediaData.MEDIA_DISPLAY_ORDER}`,
                  undefined,
                  'description',
                )}
                sx={{ fontSize: '.8rem' }}
              >
                {mediaData.MEDIA_SHORT_DESCRIPTION}
              </Typography>
            </Box>
          )}
          {/* AI Generation Progress */}
          {isGenerating && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '4px',
                bgcolor: 'rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '100%',
                  bgcolor: 'secondary.medium',
                  transition: 'width 0.3s ease-in-out',
                  animation: 'progress 1s infinite linear',
                  '@keyframes progress': {
                    '0%': {
                      width: '0%',
                      marginLeft: '0%',
                    },
                    '50%': {
                      width: '50%',
                      marginLeft: '25%',
                    },
                    '100%': {
                      width: '0%',
                      marginLeft: '100%',
                    },
                  },
                }}
              />
            </Box>
          )}
          <Divider className="bmls_divider" sx={{ mx: 3 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <MediaEditDeleteButtons
              props={{
                dataProps: mediaData,
                isConfirmCancel,
                setIsConfirmCancel,
                editCallback: showEditSidePanel,
                deleteCallback: onDeletePhoto,
                disabledIcon: <CircularProgress size={16} />,
                isEditDisabled: isGenerating,
                disabledText: 'Generating...',
                isSmallDisplay,
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            width: '10px',
            borderTop: 1.5,
            borderBottom: 1.5,
            borderRight: 1.5,
            borderColor: 'grey.400',
            borderTopRightRadius: 12,
            borderBottomRightRadius: 12,
            marginLeft: '-2px',
            display: isDraggedWithMultiple ? '' : 'none',
          }}
        ></Box>
        <Box
          sx={{
            width: '10px',
            borderTop: 1.5,
            borderBottom: 1.5,
            borderRight: 1.5,
            borderColor: 'grey.400',
            borderTopRightRadius: 12,
            borderBottomRightRadius: 12,
            marginLeft: '-2px',
            display: isDraggedWithMultiple ? '' : 'none',
          }}
        ></Box>
      </Box>
    </div>
  );
};

export default PhotoTile;
